import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Tag } from '@/client/tags';
import { useToast } from '@/hooks/useToast';
import { AxiosError } from 'axios';
import { MultiSelect } from 'primereact/multiselect';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const StyledTagsContainer = styled.div`
  width: 100%;
`;

const StyledTagsLabel = styled.label`
  display: block;
  margin-bottom: var(--small-padding);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
`;

const StyledTagsWrapper = styled.div<{ isDashboard?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: var(--small-padding);
  align-items: center;
  position: relative;

  ${({ isDashboard }) =>
    !isDashboard &&
    css`
      border: 1px solid var(--beige-main);
      background-color: var(--beige-main);
      border-radius: var(--default-border-radius);
      padding: var(--small-padding) var(--medium-padding);
      min-height: 48px;
    `}
`;

const StyledTag = styled.div`
  display: flex;
  padding: 2px 9px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  color: #2689cf;
  border-radius: 100px;
  background: #d9ebf9;

  span {
    font-size: var(--xsmall-font-size);
  }

  i {
    font-size: 10px;
  }
`;

const StyledTagName = styled.span`
  font-weight: 500;
`;

const StyledTagRemove = styled.i`
  cursor: pointer;
  font-size: var(--xsmall-font-size);
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const StyledDropdownWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledAddTagsDropdown = styled(MultiSelect)`
  border-radius: 100px !important;
  border: 1px solid var(--gray-dark) !important;
  background: var(--white-main) !important;
  height: 25px !important;
  padding: var(--xxxsmall-padding) var(--small-padding) !important;
  min-height: 0 !important;
  width: 100%;
  width: fit-content;
  font-size: var(--small-font-size);

  .p-multiselect-label {
    height: auto !important;
    background: transparent !important;
    padding: 0 !important;
    color: var(--gray-darker);
    font-size: var(--xsmall-font-size);
  }

  .p-multiselect-trigger {
    width: 20px;
    padding: 0 var(--xxsmall-padding);
    cursor: pointer !important;
  }

  .p-multiselect-label-empty {
    padding: var(--xxsmall-padding) var(--xsmall-padding);
    min-width: 80px;
  }

  .p-multiselect:not(.p-disabled):hover {
    border: none !important;
    background-color: var(--gray-pale) !important;
    border-radius: var(--small-border-radius);
  }

  .p-multiselect:not(.p-disabled).p-focus {
    border: none !important;
    box-shadow: none !important;
    background-color: var(--gray-pale) !important;
    border-radius: var(--small-border-radius);
  }

  .p-multiselect-panel {
    z-index: 1001 !important;
  }
`;

type TagsSelectorProps = {
  label?: string;
  selectedTags?: Tag[];
  disabled?: boolean;
  className?: string;
  onAddTags?: (tags: Tag[]) => void;
  onRemoveTag?: (tagId: string) => void;
  isDashboard?: boolean;
  onAvailableTagsLoaded?: (tags: Tag[]) => void;
};

export const TagsSelector: React.FC<TagsSelectorProps> = ({
  label,
  selectedTags = [],
  disabled = false,
  className,
  onAddTags,
  onRemoveTag,
  isDashboard = false,
  onAvailableTagsLoaded,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const handleSelectTags = (selectedValues: Tag[]) => {
    if (!onAddTags) return;

    onAddTags(selectedValues);
  };

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoadingTags(true);
        const response = await client.tags.getTags({
          take: 1000,
          skip: 0,
        });
        const tags = response.result || [];
        setAvailableTags(tags);
        if (onAvailableTagsLoaded) {
          onAvailableTagsLoaded(tags);
        }
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      } finally {
        setIsLoadingTags(false);
      }
    };

    if (!disabled) {
      fetchTags();
    }
  }, []);

  const dropdownOptions = availableTags.map((tag) => ({
    label: tag.translations?.find((t) => t.language === LanguagesEnum.EN)?.name,
    value: tag,
    id: tag.id,
  }));

  const isDropdownDisabled = disabled;

  return (
    <StyledTagsContainer
      className={`tags-selector-container ${className}`}
      id="tags-selector-container"
    >
      {label && <StyledTagsLabel>{label}</StyledTagsLabel>}
      <StyledTagsWrapper isDashboard={isDashboard}>
        {selectedTags.map((tag) => (
          <StyledTag key={tag.id}>
            <StyledTagName>
              {
                tag.translations?.find((t) => t.language === LanguagesEnum.EN)
                  ?.name
              }
            </StyledTagName>
            {!disabled && (
              <StyledTagRemove
                className="pi pi-times"
                onClick={() => onRemoveTag?.(tag.id)}
              />
            )}
          </StyledTag>
        ))}
        {!disabled && (
          <StyledDropdownWrapper>
            <StyledAddTagsDropdown
              value={selectedTags}
              options={dropdownOptions}
              placeholder={
                isDashboard ? t('generic.add') : t('content.tags.add')
              }
              disabled={isDropdownDisabled || isLoadingTags}
              onChange={(e) => {
                handleSelectTags(e.value || []);
              }}
              showClear={false}
              optionValue="value"
              optionLabel="label"
              dataKey="id"
              selectAll={false}
              maxSelectedLabels={0}
              selectedItemsLabel={
                isDashboard ? t('generic.add') : t('content.tags.add')
              }
              panelHeaderTemplate={isDashboard ? undefined : () => null}
            />
          </StyledDropdownWrapper>
        )}
      </StyledTagsWrapper>
    </StyledTagsContainer>
  );
};
